// components/LoginPage.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Lock, Mail, AlertCircle, Shield, KeyRound } from 'lucide-react';

const LoginPage = ({ 
  projectId, 
  projectName = 'App',
  registerPath = '/register',
  forgotPasswordPath = '/forgot-password',
  dashboardPath = '/dashboard',
  requestAccessPath = '/request-access',
  apiUrl = 'http://localhost:8000',
  brandColor = 'slate'
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [decryptionStatus, setDecryptionStatus] = useState(null);

  // Color scheme based on brandColor prop
  const colorSchemes = {
    slate: {
      gradient: 'from-slate-900 via-slate-800 to-slate-900',
      button: 'from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800',
      ring: 'focus:ring-slate-500',
      text: 'text-slate-700 hover:text-slate-900',
      lightBg: 'bg-slate-50',
      iconColor: 'text-slate-600'
    },
    blue: {
      gradient: 'from-blue-900 via-blue-800 to-blue-900',
      button: 'from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600',
      ring: 'focus:ring-blue-500',
      text: 'text-blue-600 hover:text-blue-800',
      lightBg: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    green: {
      gradient: 'from-green-900 via-green-800 to-green-900',
      button: 'from-green-600 to-green-700 hover:from-green-500 hover:to-green-600',
      ring: 'focus:ring-green-500',
      text: 'text-green-600 hover:text-green-800',
      lightBg: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    purple: {
      gradient: 'from-purple-900 via-purple-800 to-purple-900',
      button: 'from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600',
      ring: 'focus:ring-purple-500',
      text: 'text-purple-600 hover:text-purple-800',
      lightBg: 'bg-purple-50',
      iconColor: 'text-purple-600'
    }
  };

  const colors = colorSchemes[brandColor] || colorSchemes.slate;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setDecryptionStatus(null);

    try {
      setDecryptionStatus('Authenticating...');
      
      // Use the AuthContext login method
      const result = await login(formData.email, formData.password, projectId);

      if (!result.success) {
        throw new Error(result.error);
      }

      setDecryptionStatus('Success!');
      
      // Get return URL from query params, or use dashboard as fallback
      const params = new URLSearchParams(location.search);
      const returnUrl = params.get('returnUrl') || dashboardPath;
      
      // Navigate to the return URL - let ProtectedRoute handle project access
      setTimeout(() => {
        navigate(returnUrl, { replace: true });
      }, 500);

    } catch (err) {
      console.error('Login error:', err);
      
      // Handle network errors
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
      
      setDecryptionStatus(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colors.gradient} flex items-center justify-center p-4`}>
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block bg-white p-4 rounded-2xl shadow-lg mb-4">
            <div className="text-4xl font-bold text-gray-900">{projectName}</div>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Welcome Back</h2>
          <p className="text-gray-300">Sign in to your account</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Security Badge */}
          <div className={`${colors.lightBg} rounded-xl p-4 mb-6 border border-gray-100`}>
            <div className="flex items-center gap-3">
              <Shield className={`w-5 h-5 ${colors.iconColor}`} />
              <div className="text-sm">
                <p className="font-semibold text-gray-900">End-to-End Encrypted</p>
                <p className="text-gray-600">Your autentication data is decrypted locally in your browser</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Mail className="w-4 h-4" />
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
                className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${colors.ring} focus:border-transparent transition-all disabled:bg-gray-50 disabled:cursor-not-allowed`}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>

            {/* Password Field */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Lock className="w-4 h-4" />
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className={`w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${colors.ring} focus:border-transparent transition-all disabled:bg-gray-50 disabled:cursor-not-allowed`}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate(forgotPasswordPath)}
                disabled={loading}
                className={`text-sm ${colors.text} font-medium transition-colors disabled:opacity-50`}
              >
                Forgot password?
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Decryption Status */}
            {decryptionStatus && (
              <div className="bg-blue-50 border-2 border-blue-200 text-blue-700 px-4 py-3 rounded-xl flex items-center gap-3">
                {decryptionStatus === 'Success!' ? (
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                <span className="text-sm font-medium">{decryptionStatus}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-gradient-to-r ${colors.button} text-white py-4 px-6 rounded-xl font-semibold shadow-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing In...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                onClick={() => navigate(registerPath)}
                disabled={loading}
                className={`${colors.text} font-semibold transition-colors disabled:opacity-50`}
              >
                Create Account
              </button>
            </p>
          </div>

          {/* Recovery Key Option */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => navigate(`${forgotPasswordPath}?method=recovery`)}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
            >
              <KeyRound className="w-4 h-4" />
              Use Recovery Key
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">
            Your password never leaves your device
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;